// =====================================================================
// Aba 8 — Feature Flags (Fase 3, S38).
// Toggles do JSONB `features_enabled` em clinic_config.
//
// Catalogo de features (DEFINIDO em _apply_fase1_white_label_schema_s36.sh):
//   - convenio: aceita atendimento por convenio?
//   - protocols: oferece protocolos (multi-step / pacotes)?
//   - teleconsulta: tem teleconsulta?
//   - emergency: aceita emergencia (encaixe)?
//
// Default Rostand: tudo false. Default CLEE: tudo true.
// O backend salva o JSONB inteiro (PUT manda { features_enabled: {...} }).
// =====================================================================
import React, { useState, useEffect, useCallback, useMemo } from "react";
import { makeStyles } from "@material-ui/core/styles";
import {
  Box,
  Typography,
  Switch,
  FormControlLabel,
  Button,
  CircularProgress,
  Paper
} from "@material-ui/core";
import SaveIcon from "@material-ui/icons/Save";
import RefreshIcon from "@material-ui/icons/Refresh";
import LocalHospitalIcon from "@material-ui/icons/LocalHospital";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import VideocamIcon from "@material-ui/icons/Videocam";
import WhatshotIcon from "@material-ui/icons/Whatshot";
import { toast } from "react-toastify";

import {
  getClinicConfig,
  updateClinicConfig
} from "../../../services/clinicAdminService";

// Catalogo de features. Novas podem ser adicionadas aqui (mantenha key=DB).
const FEATURES = [
  {
    key: "convenio",
    label: "Atendimento por convênio",
    description: "Permite agendar via convênio (Unimed, Amil, etc).",
    Icon: AssignmentTurnedInIcon
  },
  {
    key: "protocols",
    label: "Protocolos multi-sessão",
    description: "Tratamentos em várias consultas/sessões com gatilhos e duração total.",
    Icon: LocalHospitalIcon
  },
  {
    key: "teleconsulta",
    label: "Teleconsulta",
    description: "Consultas online (vídeo). Mostra opção no formulário do paciente.",
    Icon: VideocamIcon
  },
  {
    key: "emergency",
    label: "Encaixe de emergência",
    description: "Permite marcar emergências fora da agenda regular.",
    Icon: WhatshotIcon
  }
];

const useStyles = makeStyles((theme) => ({
  root: { width: "100%" },
  list: { display: "flex", flexDirection: "column", gap: theme.spacing(1.5) },
  card: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(2),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 10,
    transition: "all 0.2s",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      background: `${theme.palette.primary.main}05`
    }
  },
  iconWrap: {
    width: 44, height: 44, borderRadius: 10,
    display: "flex", alignItems: "center", justifyContent: "center",
    background: `${theme.palette.primary.main}15`,
    color: theme.palette.primary.main,
    flexShrink: 0
  },
  content: { flex: 1, minWidth: 0 },
  label: { fontWeight: 700, fontSize: "0.9375rem" },
  description: { fontSize: "0.8125rem", color: theme.palette.text.secondary, marginTop: 2 },
  actions: {
    display: "flex",
    justifyContent: "flex-end",
    gap: theme.spacing(1),
    marginTop: theme.spacing(3)
  },
  loadingWrap: {
    display: "flex", justifyContent: "center", alignItems: "center",
    padding: theme.spacing(6)
  },
  status: {
    marginTop: theme.spacing(2),
    padding: theme.spacing(1.5, 2),
    background: `${theme.palette.warning.main}15`,
    border: `1px solid ${theme.palette.warning.main}40`,
    borderRadius: 8,
    fontSize: "0.8125rem"
  }
}));

const FeatureFlagsTab = ({ slug }) => {
  const classes = useStyles();
  const [flags, setFlags] = useState({});
  const [original, setOriginal] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!slug) return;
    try {
      setLoading(true);
      const data = await getClinicConfig(slug);
      const cfg = data && data.config ? data.config : data;
      const fe = cfg?.features_enabled || {};
      const next = {};
      FEATURES.forEach((f) => {
        next[f.key] = !!fe[f.key];
      });
      // Preserva features extras vindas do DB (forward-compat)
      Object.keys(fe).forEach((k) => {
        if (!(k in next)) next[k] = !!fe[k];
      });
      setFlags(next);
      setOriginal(next);
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || "Erro carregando flags");
    } finally {
      setLoading(false);
    }
  }, [slug]);

  useEffect(() => { load(); }, [load]);

  const toggle = (key) => (e) => {
    setFlags((prev) => ({ ...prev, [key]: e.target.checked }));
  };

  const isDirty = useMemo(() => {
    return Object.keys(flags).some((k) => !!flags[k] !== !!original[k]);
  }, [flags, original]);

  const handleSave = async () => {
    if (!isDirty) { toast.info("Nada pra salvar"); return; }
    try {
      setSaving(true);
      // Manda o JSONB inteiro (backend faz COALESCE no campo features_enabled)
      await updateClinicConfig(slug, { features_enabled: flags });
      toast.success("Feature flags atualizadas");
      await load();
    } catch (err) {
      toast.error(err?.response?.data?.error || err?.message || "Erro salvando");
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => setFlags(original);

  if (loading) return <div className={classes.loadingWrap}><CircularProgress /></div>;

  // Lista features extras (vindas do DB mas nao no catalogo)
  const extraKeys = Object.keys(flags).filter(
    (k) => !FEATURES.some((f) => f.key === k)
  );

  return (
    <div className={classes.root}>
      <Typography variant="body2" color="textSecondary" style={{ marginBottom: 16 }}>
        Ative/desative funcionalidades opcionais. Mudanças aqui refletem no
        formulário do paciente e em integrações.
      </Typography>

      <div className={classes.list}>
        {FEATURES.map((f) => {
          const Icon = f.Icon;
          return (
            <Paper elevation={0} key={f.key} className={classes.card}>
              <div className={classes.iconWrap}>
                <Icon />
              </div>
              <div className={classes.content}>
                <Typography className={classes.label}>{f.label}</Typography>
                <Typography className={classes.description}>{f.description}</Typography>
              </div>
              <Switch
                checked={!!flags[f.key]}
                onChange={toggle(f.key)}
                color="primary"
              />
            </Paper>
          );
        })}
      </div>

      {extraKeys.length > 0 && (
        <div className={classes.status}>
          <strong>⚠ Features extras encontradas no banco</strong> ({extraKeys.join(", ")}):
          {" "}preservadas no salvamento mas não aparecem na UI. Adicione ao catálogo em
          {" "}<code>FeatureFlags.js</code>.
        </div>
      )}

      <Box className={classes.actions}>
        <Button
          variant="outlined"
          startIcon={<RefreshIcon />}
          onClick={handleReset}
          disabled={!isDirty || saving}
        >
          Descartar
        </Button>
        <Button
          variant="contained"
          color="primary"
          startIcon={saving ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          onClick={handleSave}
          disabled={!isDirty || saving}
        >
          {saving ? "Salvando…" : "Salvar"}
        </Button>
      </Box>
    </div>
  );
};

export default FeatureFlagsTab;
